import { useCookies } from 'react-cookie'

import Icon from 'components/elements/Icon'
import Image from 'components/elements/Image'

import { useActionless, useAppSelector } from 'redux/hooks'

import { STACK, VIEW } from 'types/deck'
import { SET_DECK_STATE } from 'redux/deck/actions/types'
import { SET_CACHEABLES_STATE } from 'redux/cacheables/actions/types'
import { THEME } from 'types/active'

import { setLocalView, setLocalStack } from 'services/accountSettings.service'

import styles from './deckLayoutPresenter.module.scss'
import { useRightPanel } from 'hooks/usePinnedSidecar'

type Props = {}

const IMAGES = {
  'light': {
    'stacked': '/images/layouts/stack_light.webp',
    'grid': '/images/layouts/grid_light.webp',
    'text': '/images/layouts/text_light.webp',
    'textStatic': '/images/layouts/text_static_light.webp',
  },
  'dark': {
    'stacked': '/images/layouts/stack_dark.webp',
    'grid': '/images/layouts/grid_dark.webp',
    'text': '/images/layouts/text_dark.webp',
    'textStatic': '/images/layouts/text_static_dark.webp',
  },
}

const DeckLayoutPresenter = ({}: Props) => {
  const [{ tbNeverShowLayouts: closed, theme, tbId: userId }, setCookies] = useCookies([
    'tbNeverShowLayouts',
    'theme',
    'tbId',
  ])

  const view = useAppSelector(state => state.deck.view)
  const staticCard = useAppSelector(state => state.cacheables.staticCard)

  const [_, setRightPanelStatus] = useRightPanel()

  const [setDeckState, setCacheableState] = useActionless(SET_DECK_STATE, SET_CACHEABLES_STATE)

  const handleSetView = (layout: 'stacked' | 'grid' | 'text' | 'textStatic') => {
    let view = VIEW.CARD
    let stack = STACK.CUSTOM

    if (layout === 'grid') view = VIEW.GRID
    if (layout === 'text' || layout === 'textStatic') view = VIEW.TEXT

    // Change the group by to types
    if (layout === 'textStatic') stack = STACK.TYPES

    // Settings set to match thier preview images
    // tl;dr - the settings are largely arbitrary, but we're trying to show off what we can do
    setCacheableState({ disableDeckCursorCard: layout === 'textStatic', staticCard: layout === 'textStatic' })
    setRightPanelStatus({ open: layout === 'grid', locked: layout === 'grid', type: 'condensed' })

    setDeckState({ view, stack })

    setLocalView(view)
    setLocalStack(stack)
  }

  const handleClose = () => {
    setCookies('tbNeverShowLayouts', true, { path: '/', expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 365) })
  }

  const dark = `${theme}` === THEME.DARK
  const images = dark ? IMAGES.dark : IMAGES.light

  if (closed || !!userId) return null

  return (
    <div className={styles.container}>
      <p>
        Choose your preferred layout. You can mix and match options using the <span>View as & Group by</span> drop-downs
        above.
      </p>

      <div className={styles.content}>
        <Layout
          selected={view === VIEW.CARD}
          src={images.stacked}
          label="stacked"
          onClick={() => handleSetView('stacked')}
        />

        <Layout
          selected={view === VIEW.TEXT && staticCard}
          src={images.textStatic}
          label="text with static card"
          onClick={() => handleSetView('textStatic')}
        />

        <Layout
          selected={view === VIEW.TEXT && !staticCard}
          src={images.text}
          label="text"
          onClick={() => handleSetView('text')}
        />

        <Layout selected={view === VIEW.GRID} src={images.grid} label="grid" onClick={() => handleSetView('grid')} />
      </div>

      <button className={styles.closeButton} onClick={handleClose}>
        <Icon name="close" />
      </button>
    </div>
  )
}

export default DeckLayoutPresenter

const Layout = ({
  src,
  label,
  onClick,
  selected = false,
}: {
  src: string
  label: string
  onClick: () => void
  selected?: boolean
}) => (
  <div className={styles.layout}>
    <button className={styles.imageButton} onClick={onClick}>
      <Image src={src} alt={`${label} view`} className={selected ? styles.selected : undefined} />
    </button>
    <input checked={selected} type="radio" className={styles.selectButton} onClick={onClick} onChange={() => null} />
  </div>
)
